import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function DynamicForm() {
  const {
    dynamicFormModel,
    setDynamicFormModel,
    dynamicFormData,
    DYNAMIC_FORM_ENDPOINT,
    config,
    pageIndexDynamicForm,
    setPageIndexDynamicForm,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [dynamicFormDataFiltered, setDynamicFormDataFiltered] = useState(
    dynamicFormData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const dynamicFormDataObj = dynamicFormData.Data.find(
        (dynamicFormObj) => dynamicFormObj.Id === Number(id)
      );
      setDynamicFormModel(dynamicFormDataObj);
    }
  }, [id]);

  useEffect(() => {
    setDynamicFormDataFiltered(dynamicFormData.Data);
  }, [dynamicFormData]);

  const validateOperationResult = (result, isDelete) => {
    setDynamicFormModel(getClearBasicModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([DYNAMIC_FORM_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexDynamicForm !== 0 && dynamicFormModel.Data.length === 1) {
        setPageIndexDynamicForm(pageIndexDynamicForm - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${DYNAMIC_FORM_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${DYNAMIC_FORM_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${DYNAMIC_FORM_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !dynamicFormModel?.Name ||
    dynamicFormModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnsTitle = () => {
    return [
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.dynamic_forms}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={DYNAMIC_FORM_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnsTitle()}
          objectTypeName="dynamic_form"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_dynamic_form}
          onClickToSubmit={() => {
            handleUpdateRequest(dynamicFormModel);
            setShowEditModal(false);
            setDynamicFormModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setDynamicFormModel(getClearBasicModel());
          }}
          Component={
            <AddOrEditBasicEntity
              model={dynamicFormModel}
              setModel={setDynamicFormModel}
              customWidth="100%"
              nameMandatory
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.dynamic_forms}
            subtitle={localize.dynamic_form_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="dynamic_form_header_filter"
            handleAddBtn={() => {
              setDynamicFormModel(getClearBasicModel());
              setShowAddModal(true);
            }}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_dynamic_form}
                onClickToSubmit={() => {
                  handleCreateRequest(dynamicFormModel);
                  setShowAddModal(false);
                  setDynamicFormModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setDynamicFormModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    model={dynamicFormModel}
                    setModel={setDynamicFormModel}
                    customWidth="100%"
                    nameMandatory
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={dynamicFormModel.Name}
        entityType={localize.dynamic_form}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setDynamicFormModel(getClearBasicModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(dynamicFormModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexDynamicForm}
            setPageIndex={setPageIndexDynamicForm}
            dataArray={dynamicFormData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnsTitle()}
        results={
          dynamicFormData.Data?.length !== 0
            ? dynamicFormDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      detailsButtonText={localize.details}
                      onClickForEditButton={() => {
                        setDynamicFormModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setDynamicFormModel(item);
                        toggleVisibleModalConfirm();
                      }}
                      onClickForDetailButton={() => {
                        setDynamicFormModel(item);
                        navigate(`/dynamicforms/${item.Id}`);
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel data={item.Name} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  <ValueOrNoDataLabel data={item.CreatedBy} />,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
